import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { 
  Box, 
  IconButton, 
  Button, 
  CircularProgress, 
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  Link,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Paper,
  Card,
  CardContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Table from '../../components/Table/Table';
import { useNavigate } from 'react-router-dom';
import { getOrders, createMcbSession, getMcbOrderStatus, getOrderById, updateOrderStatus } from '../../api/api';
import {useToast} from '../../components/ToastProvider/ToastProvider';

const UserPortalTableContainer = ({ filters = {} }) => {
  const navigate = useNavigate();
  const { showToast } = useToast(); 
  const [rowsData, setRowsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [payingId, setPayingId] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);

  const qs = useMemo(() => {
    const params = new URLSearchParams();
    params.set('page', '1');
    params.set('per_page', '50');
    if (filters.status) params.set('status', filters.status);
    if (filters.search) params.set('search', filters.search.trim());
    if (filters.priority) params.set('priority', filters.priority);
    // Note: payment_status is client-side filtered, not sent to API
    return params.toString();
  }, [filters]);

  // Function to refresh orders data
  const refreshOrders = useCallback(async () => {
    try {
      setLoading(true);
      setErr(null);

      const res = await getOrders(qs);
      const list = Array.isArray(res?.orders) ? res.orders : [];

      // Optimized payment status fetching (same as admin portal):
      // - Only call MCB API for "AWAITING PAYMENT" status
      // - For PAID and after: show "Paid" without API call
      // - For before AWAITING PAYMENT and CANCELLED: show "-" without API call
      const withPaymentStatus = await Promise.all(
        list.map(async (o) => {
          const orderStatus = String(o.status || '').trim().toUpperCase();
          
          console.log(`[User Portal] Processing order ${o.id} (${o.order_number}), status: "${o.status}" -> normalized: "${orderStatus}"`);
          
          // Statuses before AWAITING PAYMENT or CANCELLED: show "-"
          const beforeAwaitingPayment = [
            'AWAITING VENDOR CONFIRMATION',
            'VENDOR VALIDATION REQUESTED',
            'VENDOR CONFIRMED'
          ];
          
          if (beforeAwaitingPayment.includes(orderStatus) || orderStatus === 'CANCELLED') {
            console.log(`[User Portal] Order ${o.id}: Status is before AWAITING PAYMENT or CANCELLED, showing "-"`);
            return { ...o, payment_status: 'NOT_APPLICABLE' }; // Special marker for "-"
          }
          
          // PAID and all statuses after PAID: show "Paid" without API call
          const paidAndAfter = [
            'PAID',
            'VENDOR ORDER PROCESSED',
            'ASSEMBLING ORDER',
            'SHIPPED',
            'DELIVERED'
          ];
          
          if (paidAndAfter.includes(orderStatus)) {
            console.log(`[User Portal] Order ${o.id}: Status is PAID or after, showing "Paid" without API call`);
            return { ...o, payment_status: 'PAID' }; // Directly mark as paid
          }
          
          // If order status is AWAITING PAYMENT -> call MCB API
          if (orderStatus === 'AWAITING PAYMENT') {
            try {
              console.log(`[User Portal] Order ${o.id} (${o.order_number}) is AWAITING PAYMENT -> Calling MCB API`);
              const r = await getMcbOrderStatus(o.id);
              console.log(`[User Portal] MCB API response for order ${o.id}:`, r);
              
              if (!r) {
                console.log(`[User Portal] MCB API returned null for order ${o.id}`);
                return { ...o, payment_status: 'GENERATED' };
              }
              
              // STRICT LOGIC (aligned with backend mapper):
              // - Only mark as PAID if ALL THREE are success/approved:
              //     * gateway_status in {CAPTURED, CAPTURED_PARTIALLY, PAID, COMPLETED}
              //     * result == 'SUCCESS'
              //     * payment_status == 'PAID'
              // - If payment_status is FAILED or gateway_status is FAILED -> FAILED
              // - Everything else -> GENERATED (Awaiting Payment)
              const result = String(r.result || '').toUpperCase();
              const gatewayStatus = String(r.gateway_status || '').toUpperCase();
              const paymentStatus = String(r.payment_status || '').toUpperCase();

              console.log(
                `[User Portal] MCB result for order ${o.id}: `
                + `"${result}", gateway_status: "${gatewayStatus}", payment_status: "${paymentStatus}"`
              );

              const successStatuses = ['CAPTURED', 'CAPTURED_PARTIALLY', 'PAID', 'COMPLETED'];
              const allSuccess =
                successStatuses.includes(gatewayStatus) &&
                result === 'SUCCESS' &&
                paymentStatus === 'PAID';

              const hasFailedSignal =
                gatewayStatus === 'FAILED' || paymentStatus === 'FAILED';

              if (allSuccess) {
                console.log(`[User Portal] ✅ Triple-check success -> Order ${o.id} is PAID`);
                // Backend also updates order status, but refresh local state
                return { ...o, payment_status: 'PAID', status: 'PAID' };
              }

              if (hasFailedSignal) {
                console.log(`[User Portal] ❌ MCB indicates FAILED -> Order ${o.id} marked as FAILED`);
                // Do NOT touch order.status here; only payment_status
                return { ...o, payment_status: 'FAILED' };
              }

              console.log(`[User Portal] MCB result not conclusive -> Order ${o.id} still Awaiting Payment`);
              return { ...o, payment_status: 'GENERATED' };
            } catch (error) {
              console.error(`[User Portal] Error calling MCB API for order ${o.id}:`, error);
              return { ...o, payment_status: 'GENERATED' };
            }
          }
          
          // Fallback for unknown statuses -> Awaiting Payment
          return { ...o, payment_status: 'GENERATED' };
        })
      );
      
      setRowsData(withPaymentStatus);
    } catch (e) {
      console.error('Failed to load my orders', e);
      setErr(e?.message || 'Failed to load orders');
      setRowsData([]);
      showToast('Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  }, [qs, showToast]);

  useEffect(() => {
    let alive = true;
    refreshOrders().then(() => {
      if (!alive) return;
    });
    return () => { alive = false; };
  }, [refreshOrders]);

  // Listen for payment completion message from popup window
  useEffect(() => {
    const handlePaymentMessage = (event) => {
      // Verify origin for security
      if (event.origin !== window.location.origin) return;
      
      if (event.data && event.data.type === 'PAYMENT_COMPLETE') {
        // Show success message
        if (event.data.status === 'success') {
          showToast('Payment completed successfully! Refreshing orders...', 'success');
        } else {
          showToast('Payment processing completed. Refreshing orders...', 'info');
        }
        // Refresh orders page
        setTimeout(() => {
          refreshOrders();
        }, 500);
      }
    };

    window.addEventListener('message', handlePaymentMessage);
    return () => {
      window.removeEventListener('message', handlePaymentMessage);
    };
  }, [showToast, refreshOrders]);

  function normalizePayStatus(o) {
    const raw =
      o?.payment_status ??
      o?.paymentStatus ??
      o?.payment_state ??
      (o?.is_paid ? 'PAID' : 'NOT_PAID');
    const s = String(raw || 'NOT_PAID').toUpperCase();
    if (['PAID', 'FAILED', 'PENDING', 'NOT_PAID'].includes(s)) return s;
    return 'NOT_PAID';
  }

  function PaymentBadge({ status, orderStatus }) {
    // Simplified: Only 2 statuses - "Paid" or "Awaiting Payment"
    const s = String(status || '').toUpperCase();
    let bg = '#fff8e1', fg = '#8a6d3b', label = 'Awaiting Payment';
    
    // Special case: NOT_APPLICABLE means show "-"
    if (s === 'NOT_APPLICABLE') {
      return (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            px: 1.2,
            py: 0.25,
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 600,
            backgroundColor: '#f5f5f5',
            color: '#666666',
            lineHeight: 1.2,
            minWidth: 72,
            textAlign: 'center',
          }}
        >
          —
        </Box>
      );
    }
    
    // Explicit FAILED status -> show red "Failed"
    if (s === 'FAILED') {
      bg = '#ffebee';
      fg = '#b71c1c';
      label = 'Failed';
    } else {
      // Check if order is PAID or after PAID (regardless of API status)
      const orderStatusUpper = orderStatus?.toUpperCase();
      const paidAndAfter = ['PAID', 'VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER', 'SHIPPED', 'DELIVERED'];
      
      // Only 2 main positive statuses: PAID or Awaiting Payment
      if (paidAndAfter.includes(orderStatusUpper) || s === 'PAID') {
        bg = '#e8f5e9';
        fg = '#1b5e20';
        label = 'Paid';
      } else {
        // Everything else (GENERATED, PENDING, etc.) -> Awaiting Payment
        bg = '#fff8e1';
        fg = '#8a6d3b';
        label = 'Awaiting Payment';
      }
    }

    return (
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          px: 1.2,
          py: 0.25,
          borderRadius: 999,
          fontSize: 12,
          fontWeight: 600,
          backgroundColor: bg,
          color: fg,
          lineHeight: 1.2,
          minWidth: 72,
          textAlign: 'center',
        }}
      >
        {label}
      </Box>
    );
  }

  const columns = [
    { id: 'order_number', label: 'Order #' },
    { id: 'status', label: 'Status' },
    { id: 'priority', label: 'Priority' },
    { id: 'total_amount', label: 'Total' },
    { id: 'items_count', label: 'Items' },
    { id: 'created_at', label: 'Date' },
    { id: 'payment', label: 'Payment' },
    { id: 'actions', label: 'Actions' },
  ];

  const currencyFmt = (n) => `$${Number(n || 0).toFixed(2)}`;

  // Map backend status to user-friendly timeline label (same as UserOrderManagement)
  const mapStatusToUserTimeline = (backendStatus) => {
    const statusUpper = backendStatus?.toUpperCase();
    
    // Map backend statuses to user portal timeline labels
    if (['AWAITING VENDOR CONFIRMATION', 'VENDOR VALIDATION REQUESTED'].includes(statusUpper)) {
      return 'Awaiting Order Confirmation';
    }
    if (statusUpper === 'VENDOR CONFIRMED') {
      return 'Order confirmed';
    }
    if (statusUpper === 'AWAITING PAYMENT') {
      return 'Awaiting Payment';
    }
    if (statusUpper === 'PAID') {
      return 'Paid';
    }
    if (['VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER'].includes(statusUpper)) {
      return 'Assembling Order';
    }
    if (statusUpper === 'SHIPPED') {
      return 'Shipped';
    }
    if (statusUpper === 'DELIVERED') {
      return 'Delivered';
    }
    if (statusUpper === 'CANCELLED') {
      return 'Cancelled';
    }
    // Fallback: return original status if no mapping found
    return backendStatus || 'Unknown';
  };

  // Handle Pay Now button click - open payment confirmation modal
  async function handlePay(o) {
    try {
      setSelectedOrder(o);
      setPaymentModalOpen(true);
      setTermsAccepted(false);
      setLoadingOrderDetails(true);
      
      // Fetch full order details with items
      const orderData = await getOrderById(o.id);
      setOrderDetails(orderData);
    } catch (e) {
      console.error('Failed to load order details', e);
      showToast(`Failed to load order details: ${e.message || e}`, 'error');
      setPaymentModalOpen(false);
    } finally {
      setLoadingOrderDetails(false);
    }
  }

  // Handle Proceed to Payment - open MCB payment gateway
  async function handleProceedToPayment() {
    if (!selectedOrder || !termsAccepted) return;
    
    try {
      setPayingId(selectedOrder.id);
      setPaymentModalOpen(false);

      // Prefer the freshly fetched order (orderDetails) because list rows may
      // not include credit_amount. Deduct applied credit from the total so
      // the MCB checkout session is created for the balance due, not the
      // pre-credit total. Backend re-validates this amount server-side.
      const srcOrder = orderDetails || selectedOrder;
      const total = Number(
        srcOrder?.total_amount ?? srcOrder?.total ?? srcOrder?.amount ?? 0
      );
      const credit = Number(srcOrder?.credit_amount ?? 0);
      const amount = Math.max(0, Math.round((total - credit) * 100) / 100);
      const currency = (srcOrder.currency || 'USD').toUpperCase();
      const resp = await createMcbSession({
        order_id: selectedOrder.id,
        amount,
        currency,
        description: `Payment for ${selectedOrder.order_number || selectedOrder.id}`,
      });
      
      // Open payment gateway in new window
      const paymentWindow = window.open(
        resp.checkout_url,
        'paymentGateway',
        'width=800,height=600,scrollbars=yes,resizable=yes'
      );
      
      if (!paymentWindow) {
        showToast('Please allow popups to proceed with payment', 'warning');
        return;
      }
      
      // Monitor the popup window for closure
      // PaymentReturnPage will send a message when payment completes
      const checkInterval = setInterval(() => {
        try {
          // Check if window is closed
          if (paymentWindow.closed) {
            clearInterval(checkInterval);
            // Refresh orders when popup closes (user might have completed payment)
            setTimeout(() => {
              refreshOrders();
            }, 1000);
            return;
          }
        } catch (e) {
          // Ignore errors
        }
      }, 500);
      
      // Cleanup interval after 10 minutes (payment timeout)
      setTimeout(() => {
        clearInterval(checkInterval);
      }, 600000);
      
    } catch (e) {
      console.error('Failed to start payment', e);
      showToast(`Failed to start payment: ${e.message || e}`, 'error');
    } finally {
      setPayingId(null);
      setSelectedOrder(null);
      setOrderDetails(null);
      setTermsAccepted(false);
    }
  }

  // Handle Terms and Conditions Accept
  const handleTermsAccept = () => {
    setTermsModalOpen(false);
    setTermsAccepted(true);
  };

  // Handle Terms and Conditions Deny
  const handleTermsDeny = () => {
    const confirmed = window.confirm('Are you sure you want to deny the Terms and Conditions?');
    if (confirmed) {
      setTermsModalOpen(false);
      setTermsAccepted(false);
    }
  };

  // Calculate order totals for payment confirmation modal
  const calculateOrderTotals = (order) => {
    if (!order) {
      return {
        subtotal: 0,
        shipping: 0,
        tax: 0,
        discount: 0,
        total: 0,
        credit: 0,
        amountDue: 0,
      };
    }

    const subtotal = order.subtotal || 0;
    const shipping = order.shipping_cost || 0;
    const tax = order.tax_amount || 0;
    const discount = order.discount_amount || 0;
    const total = Number(order.total_amount || 0);
    const credit = Number(order.credit_amount || 0);
    const amountDue = Math.max(0, Math.round((total - credit) * 100) / 100);

    return { subtotal, shipping, tax, discount, total, credit, amountDue };
  };

  // Filter orders by payment status (client-side filtering)
  const filteredRowsData = useMemo(() => {
    if (!filters.payment_status) return rowsData;
    
    return rowsData.filter((o) => {
      const payStatus = String(o.payment_status || '').toUpperCase();
      const orderStatusUpper = o.status?.toUpperCase();
      
      // Map to display status - Only 2 statuses: "Paid" or "Awaiting Payment"
      let displayStatus = null;
      if (payStatus === 'NOT_APPLICABLE') {
        displayStatus = '—';
      } else {
        const paidAndAfter = ['PAID', 'VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER', 'SHIPPED', 'DELIVERED'];
        if (payStatus === 'PAID' || paidAndAfter.includes(orderStatusUpper)) {
          displayStatus = 'Paid';
        } else {
          // Everything else (GENERATED, PENDING, FAILED, etc.) -> "Awaiting Payment"
          displayStatus = 'Awaiting Payment';
        }
      }
      
      return displayStatus === filters.payment_status;
    });
  }, [rowsData, filters.payment_status]);

  const rows = filteredRowsData.map((o) => {
    // Derive effective payment status
    let payStatus = String(o.payment_status || '').toUpperCase();
    if (!payStatus) payStatus = 'PENDING';
    const isPaid = payStatus === 'PAID' || payStatus === 'NOT_APPLICABLE';
    const rawStatus = String(o.status || '').toUpperCase();
    const orderStatus = rawStatus.replace('_', ' ');
    const isNotConfirmed = orderStatus === 'NOT CONFIRMED';
    const isConfirmed = orderStatus === 'CONFIRMED';

    // Visibility and enablement rules
    // Hide "Pay Now" button if:
    // - Already paid
    // - Status is NOT_APPLICABLE (before AWAITING PAYMENT or CANCELLED)
    // - Order status is PAID or after
    const orderStatusUpper = rawStatus;
    const paidAndAfter = ['PAID', 'VENDOR ORDER PROCESSED', 'ASSEMBLING ORDER', 'SHIPPED', 'DELIVERED'];
    const beforeAwaitingPayment = ['AWAITING VENDOR CONFIRMATION', 'VENDOR VALIDATION REQUESTED', 'VENDOR CONFIRMED'];
    
    const showPayNow = !isPaid && 
                       payStatus !== 'NOT_APPLICABLE' && 
                       !paidAndAfter.includes(orderStatusUpper) &&
                       !beforeAwaitingPayment.includes(orderStatusUpper) &&
                       orderStatusUpper !== 'CANCELLED';
    
    let disablePayNow = false;
    
    // Strict rules
    // 1) NOT CONFIRMED => payment Pending, button disabled
    if (isNotConfirmed) {
      payStatus = 'PENDING';
      disablePayNow = true;
    }
    // 2) CONFIRMED & not paid => NOT PAID (or FAILED/PENDING), button enabled
    else if (isConfirmed) {
      if (payStatus !== 'PAID' && payStatus !== 'NOT_APPLICABLE') {
        // If gateway still PENDING, treat as NOT PAID for UI enablement
        payStatus = (payStatus === 'FAILED') ? 'FAILED' : 'NOT_PAID';
      }
      disablePayNow = (payStatus === 'PAID' || payStatus === 'NOT_APPLICABLE');
    }
    // 3) Other statuses: disable if not applicable or already paid
    else {
      disablePayNow = (payStatus === 'NOT_APPLICABLE' || payStatus === 'PAID');
    }
    
    // Check if order has any ON_DEMAND items
    // Item is ON_DEMAND if pricing_mode is ON_DEMAND AND unit_price is 0/null/undefined
    // If unit_price > 0, it means price is confirmed, so include in calculations
    const hasOnDemandItems = Array.isArray(o.items) && o.items.some(item => {
      const unitPriceNum = Number(item.unit_price ?? item.product_price ?? 0);
      return (item.pricing_mode === 'ON_DEMAND' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined)) ||
             (item.pricing_mode !== 'FIXED' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined));
    });
    
    return {
      order_number: o.order_number,
      status: mapStatusToUserTimeline(o.status), // Use user-friendly timeline label
      priority: o.priority || 'STANDARD',
      total_amount: hasOnDemandItems ? (
        <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>Pending</Typography>
      ) : currencyFmt(o.total_amount),
      items_count: Array.isArray(o.items) ? o.items.reduce((a, it) => a + Number(it.quantity || 0), 0) : 0,
      created_at: (o.created_at || '').slice(0, 10),
      payment: <PaymentBadge status={payStatus} orderStatus={o.status} />,
      actions: (
        <Box display="flex" gap={1}>
          <IconButton
            size="small"
            onClick={() => navigate(`/user-order-management/${o.id}`)}
            title="View order"
          >
            <VisibilityIcon />
          </IconButton>

          {showPayNow && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handlePay(o)}
              sx={{ textTransform: 'none', px: 1.5, minWidth: 0 }}
              disabled={disablePayNow || payingId === o.id}
            >
              {payingId === o.id ? 'Starting…' : 'Pay Now'}
            </Button>
          )}
        </Box>
      ),
    };
  });

  return (
    <Box padding={3}>
      {loading ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={18} />
          <Typography>Loading…</Typography>
        </Box>
      ) : err ? (
        <Typography color="error">{err}</Typography>
      ) : (
        <Table columns={columns} rows={rows} title="Orders" />
      )}

      {/* Payment Confirmation Modal */}
      <Dialog 
        open={paymentModalOpen} 
        onClose={() => {}} // Prevent backdrop click from closing
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1,
          borderBottom: '1px solid #e0e0e0'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Payment Confirmation
          </Typography>
          <IconButton
            onClick={() => {
              const confirmed = window.confirm('Are you sure you want to cancel?');
              if (confirmed) {
                setPaymentModalOpen(false);
                setSelectedOrder(null);
                setOrderDetails(null);
                setTermsAccepted(false);
              }
            }}
            sx={{ 
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'action.hover' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {loadingOrderDetails ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : orderDetails ? (
            <Box>
              <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.light' }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    Order #{orderDetails.order_number}
                  </Typography>
                </CardContent>
              </Card>
              
              {/* Order Items Table */}
              <Paper sx={{ mt: 2, mb: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ bgcolor: 'grey.50', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Order Items
                  </Typography>
                </Box>
                <MuiTable size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orderDetails.items && orderDetails.items.length > 0 ? (
                      orderDetails.items
                        .filter(item => item.order_item_status !== 'exclude')
                        .map((item, idx) => {
                          const unitPrice = item.unit_price ?? item.product_price ?? 0;
                          const qty = item.quantity || 1;
                          const total = unitPrice * qty;
                          const isOnDemand = (item.pricing_mode === 'ON_DEMAND' && (unitPrice === 0 || !unitPrice)) ||
                                            (item.pricing_mode !== 'FIXED' && (unitPrice === 0 || !unitPrice));
                          
                          return (
                            <TableRow 
                              key={item.id || idx}
                              sx={{ 
                                '&:hover': { bgcolor: 'action.hover' },
                                '&:last-child td': { borderBottom: 0 }
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2">
                                  {item.snapshot_product_description || item.product_name || 'Product'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">{qty}</Typography>
                              </TableCell>
                              <TableCell align="right">
                                {isOnDemand ? (
                                  <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>
                                    Pending
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    ${unitPrice.toFixed(2)}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {isOnDemand ? (
                                  <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>
                                    Pending
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    ${total.toFixed(2)}
                                  </Typography>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No items found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </MuiTable>
              </Paper>

              {/* Pricing Summary */}
              {(() => {
                const totals = calculateOrderTotals(orderDetails);
                const hasOnDemandItems = orderDetails.items?.some(item => {
                  const unitPriceNum = Number(item.unit_price ?? item.product_price ?? 0);
                  return (item.pricing_mode === 'ON_DEMAND' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined)) ||
                         (item.pricing_mode !== 'FIXED' && (unitPriceNum === 0 || unitPriceNum === null || unitPriceNum === undefined));
                });
                
                return (
                  <Card sx={{ mt: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                    <CardContent>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                        Order Summary
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">Subtotal:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {hasOnDemandItems ? (
                            <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>Pending</Typography>
                          ) : (
                            `$${totals.subtotal.toFixed(2)}`
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">Shipping:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {(() => {
                            // Check if order is validated (AWAITING PAYMENT or later)
                            const orderStatus = orderDetails?.status || orderDetails?.order_status || '';
                            const isOrderValidated = orderStatus === 'AWAITING PAYMENT' || 
                                                     orderStatus === 'PAID' || 
                                                     orderStatus === 'VENDOR ORDER PROCESSED' || 
                                                     orderStatus === 'ASSEMBLING ORDER' || 
                                                     orderStatus === 'SHIPPED' || 
                                                     orderStatus === 'DELIVERED';
                            
                            // If order is NOT validated yet, show "To be calculated"
                            if (!isOrderValidated) {
                              return <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>To be calculated</Typography>;
                            }
                            
                            // If order is validated, show actual shipping value
                            return totals.shipping > 0 ? `$${totals.shipping.toFixed(2)}` : '$0.00';
                          })()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">Discount:</Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ color: totals.discount > 0 ? 'success.main' : 'text.primary' }}>
                          {totals.discount > 0 ? `-$${totals.discount.toFixed(2)}` : '$0.00'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="body2" color="text.secondary">VAT:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {totals.tax > 0 ? `$${totals.tax.toFixed(2)}` : '$0.00'}
                        </Typography>
                      </Box>
                      {(totals.credit || 0) > 0 && (
                        <>
                          <Divider sx={{ my: 1.5 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                              Order total (before credit):
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              ${totals.total.toFixed(2)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                            <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                              Credit applied:
                            </Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#2e7d32' }}>
                              -${Number(totals.credit).toFixed(2)}
                            </Typography>
                          </Box>
                        </>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {(totals.credit || 0) > 0 ? 'Amount due:' : 'Total:'}
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {(() => {
                            // Check if order is validated (AWAITING PAYMENT or later)
                            const orderStatus = orderDetails?.status || orderDetails?.order_status || '';
                            const isOrderValidated = orderStatus === 'AWAITING PAYMENT' ||
                                                     orderStatus === 'PAID' ||
                                                     orderStatus === 'VENDOR ORDER PROCESSED' ||
                                                     orderStatus === 'ASSEMBLING ORDER' ||
                                                     orderStatus === 'SHIPPED' ||
                                                     orderStatus === 'DELIVERED';

                            // If order has ON_DEMAND items, show "Pending"
                            if (hasOnDemandItems) {
                              return <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>Pending</Typography>;
                            }

                            // If order is NOT validated yet, show "To be calculated"
                            if (!isOrderValidated) {
                              return <Typography component="span" sx={{ color: '#ff9800', fontWeight: 600 }}>To be calculated</Typography>;
                            }

                            // Match MCB session: customer pays amount after credit
                            const shown = (totals.credit || 0) > 0 ? totals.amountDue : totals.total;
                            return `$${Number(shown).toFixed(2)}`;
                          })()}
                        </Typography>
                      </Box>
                      
                      {/* Price Disclaimer */}
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'black', 
                          fontWeight: 'bold',
                          mt: 1.5,
                          textAlign: 'right'
                        }}
                      >
                        *All prices exclude custom charges and taxes
                      </Typography>
                    </CardContent>
                  </Card>
                );
              })()}

              {/* Terms and Conditions Checkbox */}
              <Box sx={{ mt: 3, mb: 2 }}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        disabled={!termsAccepted} // Disabled by default until T&C is accepted
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Check here to indicate that you have read and agree to the{' '}
                        <Link
                          component="button"
                          variant="body2"
                          onClick={(e) => {
                            e.preventDefault();
                            setTermsModalOpen(true);
                          }}
                          sx={{ 
                            textDecoration: 'underline', 
                            cursor: 'pointer',
                            color: 'primary.main',
                            '&:hover': { color: 'primary.dark' }
                          }}
                        >
                          Terms and Conditions
                        </Link>
                      </Typography>
                    }
                  />
                </Paper>
              </Box>
            </Box>
          ) : (
            <Typography>Failed to load order details</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            onClick={() => {
              const confirmed = window.confirm('Are you sure you want to cancel?');
              if (confirmed) {
                setPaymentModalOpen(false);
                setSelectedOrder(null);
                setOrderDetails(null);
                setTermsAccepted(false);
              }
            }}
            variant="outlined"
            sx={{ minWidth: 120 }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleProceedToPayment}
            disabled={!termsAccepted || payingId === selectedOrder?.id}
            sx={{ minWidth: 180 }}
          >
            {payingId === selectedOrder?.id ? 'Starting…' : 'Proceed to Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terms and Conditions Modal */}
      <Dialog
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Sales Terms & Conditions
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
            <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                Africaplus Direct International
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Effective Date: [01/11/2025]
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>1. Introduction</strong><br />
              These Terms and Conditions of Sale ("Terms") govern all purchases made through
              the Africaplus Direct International online store ("we," "us," "our").
              By placing an order, you ("the buyer") agree to be bound by these Terms.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>2. Products and Availability</strong><br />
              All products listed on our website are subject to availability.
              We reserve the right to modify, discontinue, or adjust pricing and descriptions at any
              time without notice.
              Images and descriptions are for reference only and may vary slightly from actual
              products.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>3. Orders and Acceptance</strong><br />
              All orders submitted through our website constitute an offer to purchase.
              Africaplus Direct International reserves the right to accept or reject any order at its
              sole discretion.
              An order confirmation does not signify acceptance until payment has been received
              and verified.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>4. Pricing and Payment</strong><br />
              All prices are listed in USD and exclude applicable taxes, duties, and shipping unless
              otherwise stated.
              Payment must be made via the approved methods displayed on our site.
              We reserve the right to cancel any order suspected of fraud or unauthorized activity.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>5. Shipping and Delivery</strong><br />
              Shipping will be conducted in accordance with our Shipping Policy.
              Delivery timelines may vary by destination and carrier.
              Risk of loss or damage transfers to the buyer once the order is handed to the carrier.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>6. Returns and Refunds</strong><br />
              Returns and refunds are governed by our Returns & Refunds Policy.
              Returned items must meet the conditions outlined therein.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>7. Warranty</strong><br />
              Unless otherwise specified, Africaplus Direct International provides a limited
              warranty for new IT equipment.
              This warranty covers manufacturing defects only and excludes misuse, neglect, or
              unauthorized alterations.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>8. Limitation of Liability</strong><br />
              To the maximum extent permitted by law, Africaplus Direct International shall not be
              liable for indirect, incidental, or consequential damages arising from product use or
              purchase.
              Our total liability shall not exceed the purchase price of the item in question.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>9. Data Protection and Privacy</strong><br />
              Personal data is processed in compliance with the Data Protection Act 2017
              (Mauritius) and our Privacy Policy.
              We take all reasonable steps to ensure your data remains secure and confidential.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>10. Force Majeure</strong><br />
              We shall not be liable for failure or delay in fulfilling obligations due to circumstances
              beyond our reasonable control, including natural disasters, strikes, or transportation
              disruptions.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>11. Governing Law and Jurisdiction</strong><br />
              These Terms shall be governed by and construed in accordance with the laws of the
              Republic of Mauritius.
              Any disputes shall be subject to the exclusive jurisdiction of Mauritian courts.
            </Typography>

            <Typography variant="body2" sx={{ mb: 4 }}>
              <strong>12. Contact Us</strong><br />
              For inquiries regarding these Terms:<br />
              Africaplus Direct International<br />
              📧 Email: Online-store@africaplusdirect.com<br />
              📞 Phone: +230 260 1136/1137<br />
              📍 Address: 72 Royal Road, Coromandel, 71608, Rep. of Mauritius.<br />
              BRN: C10094108
            </Typography>

            <Divider sx={{ my: 4 }} />

            {/* Shipping Policy */}
            <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
              Shipping Policy
            </Typography>
            <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                Africaplus Direct International
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Effective Date: 01/11/2025
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>1. General Information</strong><br />
              This Shipping Policy ("Policy") governs the handling, dispatch, and delivery of
              products purchased through the Africaplus Direct International online store ("we,"
              "us," "our").
              By placing an order, you ("the buyer") agree to these terms.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>2. Processing Time</strong><br />
              Orders are typically processed within [2–5 business days] after payment
              confirmation.
              Orders placed on weekends or public holidays will be processed on the next
              business day.
              Delays may occur during high-demand periods or due to supply chain or customs
              issues.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>3. Shipping Methods and Carriers</strong><br />
              We use reputable international courier services to ensure secure delivery.
              Available shipping options and estimated delivery times are displayed at checkout.
              Buyers must provide accurate and complete shipping information.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>4. International Shipping and Customs</strong><br />
              We ship to most countries worldwide.
              Shipping costs, delivery times, and customs duties vary by destination.
              Buyers are responsible for all import taxes, duties, or other charges imposed by their
              country.
              Africaplus Direct International is not liable for delays or costs due to customs
              procedures.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>5. Shipping Costs</strong><br />
              Shipping costs are calculated at checkout based on weight, dimensions, and
              destination.
              Additional surcharges may apply for remote or restricted areas.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>6. Delivery and Risk of Loss</strong><br />
              Risk of loss, damage, or theft passes to the buyer once the order is handed to the
              carrier.
              Africaplus Direct International is not responsible for delays or damages caused by
              the carrier.
              Tracking information will be provided when available.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>7. Failed Deliveries and Incorrect Addresses</strong><br />
              If a package is returned due to an incorrect address or failure to collect within the
              carrier's holding period, the buyer is responsible for reshipping costs.
              Refunds (if applicable) are processed according to our Returns & Refunds Policy.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>8. Inspection Upon Delivery</strong><br />
              Buyers should inspect all orders upon receipt.
              Any visible damage, shortage, or discrepancy must be reported within [48 hours] of
              delivery, with supporting photos and documentation.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>9. Force Majeure</strong><br />
              Africaplus Direct International is not liable for shipping delays or non-performance
              caused by events beyond our control, including natural disasters, strikes, acts of
              government, or transportation disruptions.
            </Typography>

            <Typography variant="body2" sx={{ mb: 4 }}>
              <strong>10. Contact Us</strong><br />
              For questions or assistance regarding shipping:<br />
              Africaplus Direct International<br />
              📧 Email: Online-store@africaplusdirect.com<br />
              📞 Phone: +230 260 1136/1137<br />
              📍 Address: 72 Royal Road, Coromandel, 71608, Rep. of Mauritius.<br />
              BRN: C10094108
            </Typography>

            <Divider sx={{ my: 4 }} />

            {/* Returns & Refunds Policy */}
            <Typography variant="h6" sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
              Returns & Refunds Policy
            </Typography>
            <Box sx={{ mb: 3, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5, color: 'text.secondary', fontWeight: 500 }}>
                Africaplus Direct International
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Effective Date: 01/11/2025
              </Typography>
            </Box>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>1. General Policy</strong><br />
              This Returns & Refunds Policy ("Policy") outlines the terms under which Africaplus
              Direct International ("we," "us," "our") accepts product returns and issues refunds for
              purchases made through our online store.
              By placing an order, you ("the buyer") acknowledge and agree to this Policy.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>2. Eligibility for Returns</strong><br />
              You may request a return if:<br />
              • The product is defective, damaged upon delivery, or not as described.<br />
              • The product is listed as returnable on the product page.<br />
              • Your return request is submitted within [7–14 days] of delivery.<br />
              Returned products must be unused, in original packaging, and include all
              manuals, accessories, and proof of purchase.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>3. Non-Returnable Items</strong><br />
              The following items cannot be returned:<br />
              • Software, licenses, or digital downloads once activated.<br />
              • Opened or used consumables (e.g., printer ink, batteries).<br />
              • Items damaged through misuse, modification, or improper installation.<br />
              • Clearance or "as-is" items marked non-returnable at the time of sale.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>4. Return Procedure</strong><br />
              To initiate a return, please email Sales-support@africaplusdirect.com with:<br />
              • Order number<br />
              • Product name and serial number (if applicable)<br />
              • Photos and a brief description of the issue<br />
              After review, we will issue return authorization and shipping instructions.
              Unauthorized returns may be declined.
              Unless the return is due to our error, the buyer is responsible for shipping costs.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>5. Inspection and Approval</strong><br />
              All returned items will be inspected. Africaplus Direct International reserves the right
              to reject returns that do not meet the conditions above.
              Approved returns may result in a refund, repair, or replacement, depending on
              product availability.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>6. Refunds</strong><br />
              Refunds are issued to the original payment method within [7–14 business days]
              after inspection.
              Shipping and handling fees are non-refundable, except where the return results from
              our error.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>7. Exchanges</strong><br />
              We offer exchanges for defective or incorrect items, subject to stock availability. If a
              replacement is unavailable, a refund will be provided instead.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>8. International Returns</strong><br />
              Buyers outside Mauritius are responsible for return shipping, customs duties, and
              related fees, unless the issue is due to our fault.
              Africaplus Direct International is not liable for lost or damaged return shipments.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>9. Warranties</strong><br />
              Some IT equipment includes manufacturer warranties. Warranty claims should be
              directed to the manufacturer's authorized service center.
              We can assist with the warranty process where possible.
            </Typography>

            <Typography variant="body2" sx={{ mb: 2.5, lineHeight: 1.7 }}>
              <strong>10. Governing Law</strong><br />
              This Policy is governed by the laws of the Republic of Mauritius.
              Any disputes shall fall under the exclusive jurisdiction of Mauritian courts.
            </Typography>

            <Typography variant="body2" sx={{ mb: 4 }}>
              <strong>11. Contact Us</strong><br />
              For return or refund inquiries:<br />
              Africaplus Direct International<br />
              📧 Email: Online-store@africaplusdirect.com<br />
              📞 Phone: +230 260 1136/1137<br />
              📍 Address: 72 Royal Road, Coromandel, 71608, Rep. of Mauritius.<br />
              BRN: C10094108
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={handleTermsDeny} 
            variant="outlined"
            color="error"
            sx={{ minWidth: 100 }}
          >
            Deny
          </Button>
          <Button 
            onClick={handleTermsAccept} 
            variant="contained" 
            color="primary"
            sx={{ minWidth: 100 }}
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserPortalTableContainer;
